import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import AdminLayout from './pages/admin/AdminLayout';
import Login from './pages/admin/Login';
import Dashboard from './pages/admin/Dashboard';
import Tickets from './pages/admin/Tickets';
import AdminTicketDetail from './pages/admin/TicketDetail';
import Users from './pages/admin/Users';
import UserApproval from './pages/admin/UserApproval';
import NetworkSettings from './pages/admin/NetworkSettings';
import AdminKnowledgeBase from './pages/admin/KnowledgeBase';

import AppGuard from './pages/app/AppGuard';
import Register from './pages/app/Register';
import Waiting from './pages/app/Waiting';
import Home from './pages/app/Home';
import CreateTicket from './pages/app/CreateTicket';
import AppTicketDetail from './pages/app/TicketDetail';
import AppKnowledgeBase from './pages/app/KnowledgeBase';
import AppKnowledgeArticle from './pages/app/KnowledgeArticle';
import AppAcademy from './pages/app/Academy';
import Academy from './pages/Academy';

import Inventory from './pages/admin/Inventory';
import ContentPlan from './pages/admin/ContentPlan';
import Signage from './pages/admin/Signage';
import SmmDashboard from './pages/smm/SmmDashboard';
import SmmVK from './pages/smm/SmmVK';
import SmmTelegram from './pages/smm/SmmTelegram';
import SmmInstagram from './pages/smm/SmmInstagram';
import SmmMax from './pages/smm/SmmMax';
import SmmSettings from './pages/smm/SmmSettings';
import SmmLogin from './pages/smm/SmmLogin';
import SmmGuard from './pages/smm/SmmGuard';
import SmmAppLayout from './pages/smm/SmmAppLayout';
import { installTelegramViewport, isTelegramViewport } from './utils/telegramViewport';

import { PortalIndex, SmmIndex, SectionGuard, OwnerGuard } from './components/PortalAccess';
import './App.css';
import './Knowledge.css';
import './TelegramViewport.css';

export default function App() {
  useEffect(() => {
    const webApp = window.Telegram?.WebApp;
    const stopViewport = installTelegramViewport();
    if (!isTelegramViewport(webApp)) return stopViewport;

    // The viewport belongs to the whole portal, including admin and SMM routes.
    webApp.ready?.();
    webApp.expand?.();
    if (!window.location.pathname.startsWith('/app')) return stopViewport;

    const applyTheme = () => {
      const theme = webApp.themeParams || {};
      const root = document.documentElement;
      if (theme.bg_color) root.style.setProperty('--bg', theme.bg_color);
      if (theme.secondary_bg_color) root.style.setProperty('--bg2', theme.secondary_bg_color);
      if (theme.section_bg_color) root.style.setProperty('--card', theme.section_bg_color);
      if (theme.text_color) root.style.setProperty('--text', theme.text_color);
      if (theme.hint_color) root.style.setProperty('--text-muted', theme.hint_color);
      if (theme.button_color) root.style.setProperty('--accent', theme.button_color);
      if (theme.button_text_color) root.style.setProperty('--accent-text', theme.button_text_color);
    };

    applyTheme();
    webApp.onEvent?.('themeChanged', applyTheme);
    return () => {
      webApp.offEvent?.('themeChanged', applyTheme);
      stopViewport();
    };
  }, []);

  return (
    <Router>
      <Routes>
        {/* Admin */}
        <Route path="/admin/login" element={<Login />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<PortalIndex />} />
          <Route path="dashboard" element={<SectionGuard permission="helpdesk.dashboard"><Dashboard /></SectionGuard>} />
          <Route path="tickets" element={<SectionGuard permission="helpdesk.tickets"><Tickets /></SectionGuard>} />
          <Route path="tickets/:id" element={<SectionGuard permission="helpdesk.tickets"><AdminTicketDetail /></SectionGuard>} />
          <Route path="users" element={<SectionGuard permission="helpdesk.users"><Users /></SectionGuard>} />
          <Route path="users/:id/approve" element={<OwnerGuard><UserApproval /></OwnerGuard>} />
          <Route path="users/:id/edit" element={<OwnerGuard><UserApproval /></OwnerGuard>} />
          <Route path="settings/networks" element={<SectionGuard permission="helpdesk.networks"><NetworkSettings /></SectionGuard>} />
          <Route path="knowledge" element={<SectionGuard permission="knowledge.manage"><AdminKnowledgeBase /></SectionGuard>} />
          <Route path="inventory" element={<SectionGuard permission="inventory.manage"><Inventory /></SectionGuard>} />
          <Route path="content-plan" element={<SectionGuard permission="content_plan.manage"><ContentPlan /></SectionGuard>} />
          <Route path="signage" element={<SectionGuard permission="signage.manage"><Signage /></SectionGuard>} />
          {/* SMM Dash routes — inside AdminLayout, protected by portal section permissions */}
          <Route path="smm/dashboard" element={<SectionGuard permission="smm.overview"><SmmDashboard /></SectionGuard>} />
          <Route path="smm/vk" element={<SectionGuard permission="smm.vk"><SmmVK /></SectionGuard>} />
          <Route path="smm/telegram" element={<SectionGuard permission="smm.telegram"><SmmTelegram /></SectionGuard>} />
          <Route path="smm/instagram" element={<SectionGuard permission="smm.instagram"><SmmInstagram /></SectionGuard>} />
          <Route path="smm/max" element={<SectionGuard permission="smm.max"><SmmMax /></SectionGuard>} />
          <Route path="smm/settings" element={<SectionGuard permission="smm.settings"><SmmSettings /></SectionGuard>} />
          <Route path="academy" element={<SectionGuard permission="academy.learn"><Academy /></SectionGuard>} />
        </Route>

        {/* App (Telegram mini-app) */}
        <Route path="/app/register" element={<Register />} />
        <Route path="/app/waiting" element={<Waiting />} />
        <Route path="/app" element={<AppGuard />}>
          <Route index element={<Home />} />
          <Route path="create" element={<SectionGuard permission="helpdesk.create"><CreateTicket /></SectionGuard>} />
          <Route path="tickets/:id" element={<SectionGuard permission="helpdesk.my_tickets"><AppTicketDetail /></SectionGuard>} />
          <Route path="knowledge" element={<SectionGuard permission="knowledge.read"><AppKnowledgeBase /></SectionGuard>} />
          <Route path="knowledge/:id" element={<SectionGuard permission="knowledge.read"><AppKnowledgeArticle /></SectionGuard>} />
          <Route path="academy" element={<SectionGuard permission="academy.learn"><AppAcademy /></SectionGuard>} />
        </Route>

        {/* SMM standalone (from mini-app for eligible roles) */}
        <Route path="/smm/login" element={<SmmLogin />} />
        <Route path="/smm" element={<SmmGuard><SmmAppLayout /></SmmGuard>}>
          <Route index element={<SmmIndex />} />
          <Route path="dashboard" element={<SectionGuard permission="smm.overview"><SmmDashboard /></SectionGuard>} />
          <Route path="vk" element={<SectionGuard permission="smm.vk"><SmmVK /></SectionGuard>} />
          <Route path="telegram" element={<SectionGuard permission="smm.telegram"><SmmTelegram /></SectionGuard>} />
          <Route path="instagram" element={<SectionGuard permission="smm.instagram"><SmmInstagram /></SectionGuard>} />
          <Route path="max" element={<SectionGuard permission="smm.max"><SmmMax /></SectionGuard>} />
          <Route path="*" element={<SmmIndex />} />
        </Route>

        {/* Root redirect */}
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </Router>
  );
}
